// The tap toast on the footer buttons (touch only). A tap on a captioned tile gives no hover tooltip,
// so the new state is said in words for a moment ("Sound off", "Paused", "Cards 2 of 3") above the
// tile. One delegated `pointerup` listener records the pointer type; the following `click` on a
// `#footer_menu_left a.menu_action` (not from a mouse) reads the link's data-state / data-variant
// after React re-rendered and shows `div.tap-toast` for 1.2 s. Its own class, never `.tip-wrap`
// (the hover tests read that one). From the UI prototype (branch ui-prototype), owner's pick 2026-10-15.
import { t } from '../i18n';

const TOAST_MS = 1200;

function textFor(link: HTMLElement): string {
  const cls = [...link.classList].find((c) => c.startsWith('menu_toggle_'))?.slice('menu_toggle_'.length) ?? '';
  const on = link.dataset.state === 'on';
  switch (cls) {
    case 'sound':
      return t(on ? 'menuBottom.toast.soundOn' : 'menuBottom.toast.soundOff');
    case 'pause':
      return t(on ? 'menuBottom.toast.paused' : 'menuBottom.toast.resumed');
    case 'fullscreen':
      return t(on ? 'menuBottom.toast.fullscreenOn' : 'menuBottom.toast.fullscreenOff');
    case 'card_variant':
      return link.dataset.variant === '4' ? t('menuBottom.toast.cardsNo3d') : t('menuBottom.toast.cards', { n: link.dataset.variant ?? '1' });
    default:
      return link.dataset.caption ?? link.getAttribute('aria-label') ?? '';
  }
}

let toast: HTMLDivElement | null = null;
let hideTimer = 0;

function show(link: HTMLElement, text: string) {
  if (!text) return;
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'tap-toast';
    toast.setAttribute('role', 'status');
    document.body.appendChild(toast);
  }
  const r = link.getBoundingClientRect();
  toast.textContent = text;
  // Centred over the tile, kept 8 px inside the viewport (the first tile sits at the left edge).
  const half = toast.offsetWidth / 2 || 40;
  const x = Math.min(Math.max(r.left + r.width / 2, 8 + half), innerWidth - 8 - half);
  toast.style.left = `${Math.round(x)}px`;
  toast.style.top = `${Math.round(r.top - 8)}px`;
  toast.classList.remove('tap-toast--on');
  void toast.offsetWidth; // restart the animation on a quick second tap
  toast.classList.add('tap-toast--on');
  clearTimeout(hideTimer);
  hideTimer = window.setTimeout(() => toast?.classList.remove('tap-toast--on'), TOAST_MS);
}

export function installTapToast() {
  let lastPointer = 'mouse';
  // Not always an element: Firefox on Windows gave a target without closest() (2026-10-25 reports, Firefox 140 / 158)
  const target = (e: Event) =>
    e.target instanceof Element ? e.target.closest<HTMLElement>('#footer_menu_left a.menu_action') : null;
  document.addEventListener(
    'pointerup',
    (e) => {
      if (target(e)) lastPointer = e.pointerType || 'mouse';
    },
    true,
  );
  document.addEventListener('click', (e) => {
    const link = target(e);
    if (!link || lastPointer === 'mouse') return;
    // React re-renders the state attributes after its handler: read them on the next frame.
    requestAnimationFrame(() => show(link, textFor(link)));
  });
}
